"use no memo";

/* eslint-disable complexity */
import { isObject, get, each, isFunction, isArray } from 'lodash-es';

import { DEPLOY_ENVIRONMENTS, MARKETPLACE_ORIGINS } from 'helpers/constants';

export const DEVICE_TYPE = {
  ANDROID: 'android',
  IOS: 'ios',
};

/**
 * Returns the url of the mobile app download link based on user device
 * @param device {string} Takes in user device
 * @param restaurant {object} Takes in restaurant object
 * @returns {string} will return url as string of device appropriate app download link
 */
export function getRestaurantAppUrl(device, restaurant) {
  if (!device || !restaurant) return null;

  const isIos = device === DEVICE_TYPE.IOS;
  const iosAppId = restaurant && restaurant.ios_app_store_id;

  if (isIos && iosAppId) return `https://apps.apple.com/us/app/id${iosAppId}`;

  const isAndroid = device === DEVICE_TYPE.ANDROID;
  const androidAppUrl = restaurant && restaurant.android_app_url;

  if (isAndroid && androidAppUrl) return androidAppUrl;

  return null;
}

export function isDirectUrl() {
  const DIRECT_REGEX = /(direct)|(order)/;
  return DIRECT_REGEX.test(window.location.hostname);
}

export function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/**
 * Correctly rewrite and format a hostname for Marketplace
 * accounting for both the app name and prod vs QA environments.
 * @returns {string} of hostname
 */
export function getHostnameForMarketplace() {
  let deployEnv = process.env.REACT_APP_DEPLOY_ENV;
  if (deployEnv === DEPLOY_ENVIRONMENTS.prod) {
    return `${MARKETPLACE_ORIGINS.prod}.chownow.com`;
  }

  if (!deployEnv && process.env.NODE_ENV === 'test') {
    deployEnv = 'test';
  }

  return `${MARKETPLACE_ORIGINS.qa}.${deployEnv}.svpn.chownow.com`;
}

export function concatAddressWithAddressLine2({
  formattedAddress,
  addressLine2,
}) {
  const indexOfFirstComma = formattedAddress.indexOf(',');
  return [
    formattedAddress.slice(0, indexOfFirstComma + 1),
    ' ',
    addressLine2,
    formattedAddress.slice(indexOfFirstComma),
  ].join('');
}

export function getHqIdFromUrl() {
  const hqIdRegexPattern = /(?:order|dine-in|catering)\/(\d+)/;
  const result = window.location.href.match(hqIdRegexPattern);

  return get(result, '[1]');
}

/** @returns {string | undefined} the extracted orderId from the URL */
export function getOrderIdFromUrl(url = window.location.href) {
  return /history\/([^/?#]+)/.exec(url)?.[1];
}

/**
 * @param {string} [url] the url from which to parse the resto ID from (defaults to window.location.href)
 * @returns {string | undefined}
 */
export function getRestaurantIdFromUrl(url = window.location.href) {
  const isDiscover = process.env.REACT_APP_PACKAGE === 'Marketplace-Web';
  let restaurantIdRegexPattern;
  if (isDiscover) {
    restaurantIdRegexPattern = /restaurant\/(\d+)/;
  } else {
    restaurantIdRegexPattern = /locations\/(\d+)/;
  }
  const result = url.match(restaurantIdRegexPattern);
  return result?.[1];
}

// loose check for path in pathname
// will return true if path is in the middle of the pathname as well as beginning
export function isOnPage(path) {
  return window.location.pathname.includes(path);
}

export function isOnMenu() {
  const hqId = getHqIdFromUrl();
  const restaurantId = getRestaurantIdFromUrl();
  const isCheckout = isOnPage('checkout');
  const isInquiry = isOnPage('inquiry');

  return !!hqId && !!restaurantId && !isCheckout && !isInquiry;
}

export function isOnLocations() {
  const hqId = getHqIdFromUrl();
  const restaurantId = getRestaurantIdFromUrl();
  const isSupport = isOnPage('support');

  return !!hqId && !restaurantId && !isSupport;
}

export function getDeviceType() {
  let device;

  if (
    /iPad|iPhone/i.test(navigator.userAgent) ||
    navigator.userAgent.includes('Mac') ||
    navigator.userAgent.match('CriOS') // check for chrome on iOS
  ) {
    device = DEVICE_TYPE.IOS;
  } else if (/Android/i.test(navigator.userAgent)) {
    device = DEVICE_TYPE.ANDROID;
  }

  return device;
}

function serialize(params, obj, traditional, scope) {
  let type;
  const array = isArray(obj);
  const hash = isObject(obj);
  // eslint-disable-next-line func-names, prefer-arrow-callback
  each(obj, function (value, key) {
    type = typeof value;
    if (scope) {
      key = traditional // eslint-disable-line no-param-reassign
        ? scope
        : scope + // eslint-disable-line
          '[' +
          (hash || type === 'object' || type === 'array' ? key : '') +
          ']';
    }
    // handle data in serializeArray() format
    if (!scope && array) {
      params.add(value.name, value.value);
    } else if (type === 'array' || (!traditional && type === 'object')) {
      // recurse into nested objects
      serialize(params, value, traditional, key);
    } else {
      params.add(key, value);
    }
  });
}

/* eslint-disable */
export function param(obj, traditional) {
  let params = [];
  params.add = function (key, value) {
    if (isFunction(value)) value = value();
    if (value == null) value = '';
    this.push(escape(key) + '=' + escape(value));
  };
  serialize(params, obj, traditional);
  return params.join('&').replace(/%20/g, '+');
}
/* eslint-enable */

export function isIE() {
  return (
    navigator.appName === 'Microsoft Internet Explorer' ||
    (navigator.appName === 'Netscape' &&
      // eslint-disable-next-line prefer-regex-literals
      new RegExp('Trident/.*rv:([0-9]{1,}[.0-9]{0,})').exec(
        navigator.userAgent
      ) !== null)
  );
}

export function appendUtmSource(url, utmSource) {
  const parsed = new URL(url, window.location.href);
  parsed.searchParams.set('utm_source', utmSource);
  return parsed.toString();
}

export function getIsChrome() {
  return (
    /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
  );
}
